<template>
  <span
    v-if="darfMassenmailVersenden"
    class="massen-email-dialog d-inline-block"
  >
    <v-dialog
      ref="massenDialog"
      v-model="dialog"
      scrollable
      :persistent="closeDisabled"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="1120"
    >
      <template v-slot:activator="{ attrs }">
        <v-btn
          small
          color="primary"
          v-bind="attrs"
          :disabled="offline"
          :title="offline
            ? 'Der Massen-E-Mail-Versand ist offline nicht verfügbar.'
            : 'Massen-E-Mail erstellen'"
          @click="openDialog"
        >
          <v-icon left>
            mdi-email-multiple-outline
          </v-icon>
          Massen-E-Mail
        </v-btn>
      </template>

      <v-card>
        <v-card-title id="massen-email-dialog-title" class="primary white--text">
          <v-icon left dark>
            mdi-email-multiple-outline
          </v-icon>
          Massen-E-Mail versenden
          <v-spacer></v-spacer>
          <v-btn
            icon
            dark
            aria-label="Dialog schließen"
            :disabled="closeDisabled"
            @click="closeDialog"
          >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-progress-linear
          v-if="loadingInitial"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <v-card-text class="pa-0">
          <v-alert
            v-if="offline"
            type="error"
            tile
            class="ma-0"
          >
            Der Versand ist ohne Netzwerkverbindung nicht möglich.
          </v-alert>
          <v-alert
            v-if="globalError"
            type="error"
            text
            class="ma-4 mb-0"
          >
            {{ globalError }}
          </v-alert>

          <v-stepper
            v-model="step"
            class="elevation-0"
          >
            <v-stepper-header>
              <v-stepper-step
                :complete="step > 1"
                step="1"
              >
                Empfänger
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="step > 2"
                step="2"
              >
                Inhalt
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="step > 3"
                step="3"
              >
                Prüfen
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="4">
                Versand
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1">
                <p class="text-h6 mb-1">
                  Empfängerquellen
                </p>
                <p class="text-caption text--secondary">
                  Die Empfänger werden beim Ermitteln anhand der aktuellen Serverdaten geladen.
                </p>

                <v-row dense>
                  <v-col
                    v-for="source in sourceOptions"
                    :key="source.value"
                    cols="12"
                    md="4"
                  >
                    <v-checkbox
                      v-model="selectedSources"
                      :value="source.value"
                      :label="source.label"
                      hide-details
                      class="mt-0"
                      :disabled="loadingRecipients"
                      @change="markRecipientsStale"
                    />
                    <p class="text-caption text--secondary ml-8 mt-1 mb-0">
                      {{ source.description }}
                    </p>
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap align-center mt-4">
                  <v-btn
                    small
                    color="primary"
                    :loading="loadingRecipients"
                    :disabled="offline || selectedSources.length <= 0"
                    @click="loadRecipients"
                  >
                    <v-icon left>
                      mdi-account-search
                    </v-icon>
                    Empfänger ermitteln
                  </v-btn>
                  <span
                    v-if="!recipientsLoaded && selectedSources.length > 0"
                    class="text-caption warning--text ml-3"
                  >
                    Auswahl geändert – bitte Empfänger neu ermitteln.
                  </span>
                </div>

                <v-divider class="my-6"></v-divider>

                <p class="text-h6 mb-1">
                  Manuelle Empfänger
                </p>
                <v-combobox
                  v-model="manualEntries"
                  multiple
                  chips
                  deletable-chips
                  clearable
                  outlined
                  dense
                  :delimiters="[',', ';']"
                  label="E-Mail-Adressen"
                  :hint="`Adresse eingeben und mit Enter bestätigen;
                    mehrere Adressen mit Komma trennen.`"
                  persistent-hint
                  :disabled="enqueueing || batchActive"
                  @change="removeUnknownExclusions"
                />

                <v-alert
                  v-if="invalidManualAddresses.length > 0"
                  dense
                  text
                  type="error"
                  class="mt-3"
                >
                  Ungültige manuelle Adressen: {{ invalidManualAddresses.join(', ') }}
                </v-alert>
                <v-alert
                  v-if="invalidBackendRecipients.length > 0"
                  dense
                  text
                  type="warning"
                  class="mt-3"
                >
                  {{ invalidBackendRecipients.length }} vom Server gelieferte Adresse(n) waren
                  ungültig und wurden nicht übernommen.
                </v-alert>

                <div class="d-flex flex-wrap mt-4">
                  <v-chip
                    small
                    color="primary"
                    dark
                    class="mr-2 mb-2"
                  >
                    {{ allRecipients.length }} eindeutig insgesamt
                  </v-chip>
                  <v-chip
                    small
                    color="success"
                    dark
                    class="mr-2 mb-2"
                  >
                    {{ selectedRecipients.length }} ausgewählt
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    class="mr-2 mb-2"
                  >
                    {{ automaticRecipients.length }} automatisch
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    class="mr-2 mb-2"
                  >
                    {{ manualRecipients.length }} manuell
                  </v-chip>
                  <v-chip
                    v-for="metric in serverMetricItems"
                    :key="metric.key"
                    small
                    outlined
                    class="mr-2 mb-2"
                  >
                    {{ metric.label }}: {{ metric.metricValue }}
                  </v-chip>
                </div>

                <v-alert
                  v-if="recipientLimitExceeded"
                  dense
                  type="error"
                  text
                  class="mt-2"
                >
                  Es können höchstens {{ maxRecipients }} Empfänger pro Versand ausgewählt werden.
                  Bitte die Auswahl reduzieren.
                </v-alert>

                <v-alert
                  v-if="missing.length > 0"
                  dense
                  type="warning"
                  text
                  class="mt-2"
                >
                  <b>{{ metrics.missingCount || missing.length }} Eintrag/Einträge ohne nutzbare
                    E-Mail-Adresse:</b>
                  <span v-if="metrics.missingCount > missing.length">
                    (angezeigt werden die ersten {{ missing.length }})
                  </span>
                  <ul class="mt-2 mb-0">
                    <li
                      v-for="(entry, index) in missing"
                      :key="`missing-${index}`"
                    >
                      {{ missingLabel(entry) }}
                    </li>
                  </ul>
                </v-alert>

                <v-alert
                  v-if="Number(metrics.truncatedCount) > 0"
                  dense
                  type="warning"
                  text
                  class="mt-2"
                >
                  Die Prüfliste wurde aus Sicherheitsgründen auf 2.000 Adressen begrenzt.
                  {{ metrics.truncatedCount }} weitere Adresse(n) werden nicht für diesen Versand
                  angeboten.
                </v-alert>

                <v-data-table
                  v-if="allRecipients.length > 0"
                  dense
                  item-key="email"
                  class="mt-4"
                  :headers="recipientHeaders"
                  :items="allRecipients"
                  :items-per-page="25"
                  :footer-props="footerProps"
                  no-data-text="Keine Empfänger gefunden"
                >
                  <template v-slot:header.selected>
                    <v-checkbox
                      dense
                      hide-details
                      aria-label="Alle Empfänger auswählen oder abwählen"
                      :input-value="allRecipientsSelected"
                      :indeterminate="someRecipientsSelected && !allRecipientsSelected"
                      @change="toggleAllRecipients"
                    />
                  </template>
                  <template v-slot:item.selected="{ item }">
                    <v-checkbox
                      dense
                      hide-details
                      :aria-label="`${item.email} auswählen oder abwählen`"
                      :input-value="isRecipientSelected(item)"
                      @change="toggleRecipient(item)"
                    />
                  </template>
                  <template v-slot:item.name="{ item }">
                    {{ item.name || '—' }}
                  </template>
                  <template v-slot:item.customer="{ item }">
                    <span>{{ item.customer || '—' }}</span>
                    <span
                      v-if="item.customerNumber"
                      class="text-caption text--secondary"
                    >
                      ({{ item.customerNumber }})
                    </span>
                  </template>
                  <template v-slot:item.sources="{ item }">
                    <v-chip
                      v-for="source in item.sources"
                      :key="`${item.email}-${source}`"
                      x-small
                      outlined
                      class="mr-1 my-1"
                    >
                      {{ sourceLabel(source) }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-stepper-content>

              <v-stepper-content step="2">
                <p class="text-h6 mb-3">
                  Absender und Inhalt
                </p>

                <v-alert
                  :type="smtpReady ? 'info' : 'error'"
                  text
                  dense
                >
                  <div>
                    <b>Absender:</b>
                    {{ senderLabel }}
                  </div>
                  <div v-if="!smtpReady" class="mt-1">
                    Das SMTP-Postfach ist nicht vollständig konfiguriert. Ein Versand ist nicht
                    möglich.
                  </div>
                </v-alert>

                <v-form
                  ref="mailForm"
                  v-model="mailFormValid"
                >
                  <v-text-field
                    v-model="subject"
                    outlined
                    counter="200"
                    maxlength="200"
                    label="Betreff"
                    :rules="subjectRules"
                    :disabled="enqueueing || batchActive"
                  />

                  <p class="text-subtitle-1 font-weight-medium mb-2">
                    Nachricht
                  </p>
                  <HtmlEditor
                    v-model="bodyHtml"
                    label="E-Mail-Nachricht"
                    placeholder="Nachricht eingeben ..."
                    :disabled="enqueueing || batchActive"
                    @error="handleEditorError"
                  />
                  <v-alert
                    v-if="editorError"
                    dense
                    text
                    type="error"
                    class="mt-3"
                  >
                    {{ editorError }}
                  </v-alert>
                  <v-alert
                    v-if="bodyTooLarge"
                    dense
                    text
                    type="error"
                    class="mt-3"
                  >
                    Die Nachricht ist inklusive eingefügter Bilder größer als 5 MB.
                    Bitte Bilder verkleinern oder entfernen.
                  </v-alert>
                </v-form>

                <v-divider class="my-6"></v-divider>

                <p class="text-subtitle-1 font-weight-medium mb-2">
                  Signaturvorschau
                </p>
                <p class="text-caption text--secondary">
                  Diese zentral konfigurierte Signatur wird beim Versand serverseitig angehängt.
                </p>
                <iframe
                  v-if="summary.signatureHtml"
                  class="mail-preview-frame"
                  title="Vorschau der E-Mail-Signatur"
                  sandbox=""
                  referrerpolicy="no-referrer"
                  :srcdoc="signatureDocument"
                ></iframe>
                <v-alert
                  v-else
                  dense
                  text
                  type="info"
                >
                  Es ist keine E-Mail-Signatur hinterlegt.
                </v-alert>
              </v-stepper-content>

              <v-stepper-content step="3">
                <p class="text-h6 mb-3">
                  Versand abschließend prüfen
                </p>

                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <th>Absender</th>
                      <td>{{ senderLabel }}</td>
                    </tr>
                    <tr>
                      <th>Empfänger</th>
                      <td>{{ selectedRecipients.length }} eindeutige Adresse(n)</td>
                    </tr>
                    <tr>
                      <th>Betreff</th>
                      <td>{{ subject }}</td>
                    </tr>
                    <tr>
                      <th>Signatur</th>
                      <td>{{ summary.signatureHtml ? 'Wird angehängt' : 'Keine hinterlegt' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <p class="text-subtitle-1 font-weight-medium mt-6 mb-2">
                  Nachrichtenvorschau
                </p>
                <iframe
                  class="mail-preview-frame mail-preview-frame--message"
                  title="Vorschau der vollständigen E-Mail"
                  sandbox=""
                  referrerpolicy="no-referrer"
                  :srcdoc="emailPreviewDocument"
                ></iframe>

                <v-alert
                  type="info"
                  text
                  class="mt-5"
                >
                  Jede Adresse erhält eine eigene E-Mail. Die Empfänger sehen keine Adressen
                  anderer Empfänger.
                </v-alert>

                <v-checkbox
                  v-model="confirmation"
                  :disabled="enqueueing"
                  :label="`Ich habe die ${selectedRecipients.length} Empfänger geprüft und
                    bestätige den Einzelversand.`"
                />

                <v-alert
                  v-if="enqueueError"
                  dense
                  text
                  type="error"
                >
                  {{ enqueueError }}
                </v-alert>
              </v-stepper-content>

              <v-stepper-content step="4">
                <div
                  v-if="batch.id"
                  aria-live="polite"
                >
                  <div class="d-flex flex-wrap align-center mb-4">
                    <p class="text-h6 mb-0 mr-3">
                      Versandstatus
                    </p>
                    <v-chip
                      small
                      dark
                      :color="batchStatusColor"
                    >
                      {{ batchStatusLabel }}
                    </v-chip>
                  </div>

                  <v-progress-linear
                    height="24"
                    rounded
                    :value="batchProgress"
                    :indeterminate="batch.total <= 0 && batchActive"
                    color="primary"
                  >
                    <strong v-if="batch.total > 0">
                      {{ Math.round(batchProgress) }} %
                    </strong>
                  </v-progress-linear>

                  <v-row class="mt-4" dense>
                    <v-col cols="6" sm="3">
                      <v-card outlined>
                        <v-card-text class="text-center">
                          <div class="text-h5">
                            {{ batch.total }}
                          </div>
                          <div class="text-caption">
                            Gesamt
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-card outlined>
                        <v-card-text class="text-center success--text">
                          <div class="text-h5">
                            {{ batch.sent }}
                          </div>
                          <div class="text-caption">
                            Versendet
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-card outlined>
                        <v-card-text class="text-center error--text">
                          <div class="text-h5">
                            {{ batch.failed }}
                          </div>
                          <div class="text-caption">
                            Fehlgeschlagen
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-card outlined>
                        <v-card-text class="text-center warning--text">
                          <div class="text-h5">
                            {{ batch.pending }}
                          </div>
                          <div class="text-caption">
                            Ausstehend
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-alert
                    v-if="batchIsTerminal"
                    :type="batch.failed > 0 ? 'warning' : 'success'"
                    text
                    class="mt-5"
                  >
                    <template v-if="batch.failed > 0">
                      Der Versand wurde abgeschlossen. {{ batch.failed }} E-Mail(s) konnten nicht
                      versendet werden.
                    </template>
                    <template v-else>
                      Alle {{ batch.sent }} E-Mail(s) wurden versendet.
                    </template>
                  </v-alert>

                  <v-alert
                    v-else
                    type="info"
                    text
                    class="mt-5"
                  >
                    Der Versand läuft serverseitig weiter. Sie können diesen Dialog schließen.
                  </v-alert>

                  <v-alert
                    v-if="statusError"
                    dense
                    text
                    type="error"
                    class="mt-5"
                  >
                    {{ statusError }}
                  </v-alert>
                  <v-btn
                    v-if="statusError && !batchIsTerminal"
                    small
                    color="primary"
                    :loading="polling"
                    :disabled="offline"
                    @click="retryStatus"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    Status erneut abrufen
                  </v-btn>
                </div>
                <div v-else-if="recoveryRequestId">
                  <v-alert
                    :type="statusError ? 'warning' : 'info'"
                    text
                  >
                    <strong>Der Ausgang eines vorherigen Versandstarts ist noch ungeklärt.</strong>
                    Es wird kein neuer Versand gestartet, solange der Server diesen Start nicht
                    eindeutig zugeordnet hat. Prüfen Sie den Status erneut; die Kennung bleibt
                    auch nach dem Schließen dieses Dialogs gespeichert.
                    <span v-if="pendingRecoveryCount > 1">
                      Zusätzlich sind {{ pendingRecoveryCount - 1 }} weitere Versandstarts zur
                      Prüfung gespeichert.
                    </span>
                  </v-alert>
                  <v-alert
                    v-if="statusError"
                    dense
                    text
                    type="warning"
                  >
                    {{ statusError }}
                  </v-alert>
                  <v-btn
                    small
                    color="primary"
                    class="mr-2 mb-2"
                    :loading="recoveringPending"
                    :disabled="offline"
                    @click="retryRecovery"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    Status erneut prüfen
                  </v-btn>
                  <v-btn
                    small
                    text
                    color="warning"
                    class="mb-2"
                    :disabled="recoveringPending"
                    @click="discardPendingRecovery"
                  >
                    Ungeklärten Start verwerfen
                  </v-btn>
                </div>
                <v-alert
                  v-else
                  type="error"
                  text
                >
                  Der Server hat keinen Versandstatus geliefert.
                </v-alert>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="step === 2 || step === 3"
            text
            :disabled="enqueueing || batchActive"
            @click="goBack"
          >
            <v-icon left>
              mdi-arrow-left
            </v-icon>
            Zurück
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="step < 4"
            text
            :disabled="closeDisabled"
            @click="closeDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            v-if="step === 1"
            color="primary"
            :disabled="!canContinueToContent"
            @click="goToContent"
          >
            Inhalt
            <v-icon right>
              mdi-arrow-right
            </v-icon>
          </v-btn>
          <v-btn
            v-if="step === 2"
            color="primary"
            :disabled="!canContinueToReview"
            @click="goToReview"
          >
            Prüfen
            <v-icon right>
              mdi-arrow-right
            </v-icon>
          </v-btn>
          <v-btn
            v-if="step === 3"
            color="success"
            :loading="enqueueing"
            :disabled="!canEnqueue"
            @click="enqueue"
          >
            <v-icon left>
              mdi-send
            </v-icon>
            {{ enqueueError ? 'Versand erneut anstoßen' : 'Jetzt versenden' }}
          </v-btn>
          <v-btn
            v-if="step === 4"
            color="primary"
            :disabled="closeDisabled"
            @click="closeDialog"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import HtmlEditor from '@/components/Funktionen/400/410-Kunden/HtmlEditor.vue';

const MAX_RECIPIENTS = 500;
const MAX_BODY_LENGTH = 5 * 1024 * 1024;
const EMAIL_PATTERN = /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i;
const TERMINAL_BATCH_STATUSES = [
  'complete',
  'completed',
  'completed_with_errors',
  'done',
  'finished',
  'failed',
  'cancelled',
  'canceled',
];

function emptySummary() {
  return {
    fromName: '',
    fromMail: '',
    smtpConfigured: false,
    signatureHtml: '',
    updatedAt: 0,
  };
}

function emptyBatch() {
  return {
    id: '',
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    status: '',
  };
}

export default {
  name: 'MassenEmailDialog',
  components: {
    HtmlEditor,
  },
  data: () => ({
    maxRecipients: MAX_RECIPIENTS,
    dialog: false,
    step: 1,
    offline: typeof navigator !== 'undefined' && navigator.onLine === false,
    loadingSummary: false,
    loadingRecipients: false,
    enqueueing: false,
    polling: false,
    recoveringPending: false,
    pollTimer: null,
    requestGeneration: 0,
    requestControllers: [],
    summaryError: '',
    recipientsError: '',
    editorError: '',
    enqueueError: '',
    statusError: '',
    summary: emptySummary(),
    selectedSources: ['active_contracts'],
    sourceOptions: [
      {
        value: 'active_contracts',
        label: 'Aktive Verträge',
        description: 'Alle E-Mail-Kontakte der Kunden mit aktiven Verträgen ermitteln.',
      },
      {
        value: 'planned_documents',
        label: 'Geplante Dokumente',
        description: 'Hinterlegte Versandempfänger geplanter Dokumente verwenden.',
      },
      {
        value: 'all_customers',
        label: 'Alle Kunden',
        description: 'Alle verfügbaren Kundenkontakte berücksichtigen.',
      },
    ],
    recipientsLoaded: false,
    automaticRecipients: [],
    invalidBackendRecipients: [],
    metrics: {},
    missing: [],
    manualEntries: [],
    excludedEmails: [],
    recipientHeaders: [
      {
        text: '', value: 'selected', sortable: false, width: '50px',
      },
      { text: 'Name', value: 'name' },
      { text: 'E-Mail', value: 'email' },
      { text: 'Kunde', value: 'customer' },
      { text: 'Quelle', value: 'sources', sortable: false },
    ],
    footerProps: {
      itemsPerPageText: 'Empfänger pro Seite:',
      itemsPerPageOptions: [10, 25, 50, 100, -1],
      itemsPerPageAllText: 'Alle',
    },
    mailFormValid: false,
    subject: '',
    bodyHtml: '',
    subjectRules: [
      (value) => Boolean(value && String(value).trim()) || 'Bitte einen Betreff angeben.',
      (value) => String(value || '').length <= 200 || 'Maximal 200 Zeichen erlaubt.',
    ],
    confirmation: false,
    enqueuePayload: null,
    batch: emptyBatch(),
    recoveryRequestId: '',
    pendingRecoveryCount: 0,
  }),
  computed: {
    globalError() {
      return [this.summaryError, this.recipientsError].filter(Boolean).join(' ');
    },
    darfMassenmailVersenden() {
      const mainSave = this.$store.state.main.save;
      const rights = mainSave && mainSave.user && Array.isArray(mainSave.user.rechte)
        ? mainSave.user.rechte : [];
      return rights.includes('s|410|mail')
        && (rights.includes('x') || rights.includes('x|410|000'));
    },
    loadingInitial() {
      return this.loadingSummary || this.loadingRecipients;
    },
    smtpReady() {
      return Boolean(
        this.summary.smtpConfigured
        && this.summary.fromMail
        && this.isValidEmail(this.summary.fromMail),
      );
    },
    senderLabel() {
      const name = this.summary.fromName ? `${this.summary.fromName} ` : '';
      return this.summary.fromMail ? `${name}<${this.summary.fromMail}>` : 'Nicht konfiguriert';
    },
    manualTokens() {
      const tokens = [];
      this.manualEntries.forEach((entry) => {
        let value = entry;
        if (entry && typeof entry === 'object') {
          value = entry.text || entry.value || '';
        }
        String(value || '').split(/[\s,;]+/).forEach((token) => {
          if (token.trim()) {
            tokens.push(token.trim());
          }
        });
      });
      return tokens;
    },
    invalidManualAddresses() {
      return [...new Set(this.manualTokens
        .filter((email) => !this.isValidEmail(email)))];
    },
    manualRecipients() {
      const recipientsByEmail = new Map();
      this.manualTokens.forEach((email) => {
        const normalizedEmail = this.normalizeEmail(email);
        if (this.isValidEmail(normalizedEmail) && !recipientsByEmail.has(normalizedEmail)) {
          recipientsByEmail.set(normalizedEmail, {
            email: normalizedEmail,
            name: '',
            customer: 'Manuell eingegeben',
            customerNumber: '',
            sources: ['manual'],
          });
        }
      });
      return Array.from(recipientsByEmail.values());
    },
    allRecipients() {
      const recipientsByEmail = new Map();
      [...this.automaticRecipients, ...this.manualRecipients].forEach((recipient) => {
        const email = this.normalizeEmail(recipient.email);
        if (!this.isValidEmail(email)) {
          return;
        }
        const sources = Array.isArray(recipient.sources) ? recipient.sources : [];
        if (!recipientsByEmail.has(email)) {
          recipientsByEmail.set(email, {
            email,
            name: recipient.name || '',
            customer: recipient.customer || '',
            customerNumber: recipient.customerNumber || '',
            sources: [...new Set(sources)],
          });
          return;
        }
        const existing = recipientsByEmail.get(email);
        existing.sources = [...new Set([...existing.sources, ...sources])];
        if (!existing.name && recipient.name) {
          existing.name = recipient.name;
        }
        if (!existing.customer && recipient.customer) {
          existing.customer = recipient.customer;
        }
        if (!existing.customerNumber && recipient.customerNumber) {
          existing.customerNumber = recipient.customerNumber;
        }
      });
      return Array.from(recipientsByEmail.values())
        .sort((first, second) => first.email.localeCompare(second.email, 'de'));
    },
    selectedRecipients() {
      return this.allRecipients.filter((recipient) => this.isRecipientSelected(recipient));
    },
    allRecipientsSelected() {
      return this.allRecipients.length > 0
        && this.selectedRecipients.length === this.allRecipients.length;
    },
    someRecipientsSelected() {
      return this.selectedRecipients.length > 0;
    },
    recipientLimitExceeded() {
      return this.selectedRecipients.length > MAX_RECIPIENTS;
    },
    sourceSelectionCurrent() {
      return this.selectedSources.length <= 0 || this.recipientsLoaded;
    },
    canContinueToContent() {
      return !this.offline
        && !this.loadingRecipients
        && this.sourceSelectionCurrent
        && this.selectedRecipients.length > 0
        && !this.recipientLimitExceeded
        && this.invalidManualAddresses.length <= 0;
    },
    bodyHasContent() {
      const plainText = this.bodyHtml
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return plainText.length > 0 || /<img\b/i.test(this.bodyHtml);
    },
    bodyTooLarge() {
      return this.bodyHtml.length > MAX_BODY_LENGTH;
    },
    canContinueToReview() {
      return !this.offline
        && this.smtpReady
        && this.mailFormValid
        && this.subject.trim().length > 0
        && this.bodyHasContent
        && !this.bodyTooLarge
        && this.selectedRecipients.length > 0
        && !this.recipientLimitExceeded;
    },
    canEnqueue() {
      return this.canContinueToReview
        && this.confirmation
        && !this.enqueueing
        && !this.batchActive;
    },
    signatureDocument() {
      return this.previewDocument(this.summary.signatureHtml || '');
    },
    emailPreviewDocument() {
      const signature = this.summary.signatureHtml
        ? `<div class="signature">${this.summary.signatureHtml}</div>` : '';
      return this.previewDocument(`${this.bodyHtml}${signature}`);
    },
    serverMetricItems() {
      if (!this.metrics || typeof this.metrics !== 'object') {
        return [];
      }
      return Object.keys(this.metrics)
        .filter((key) => ['string', 'number'].includes(typeof this.metrics[key]))
        .map((key) => ({
          key,
          label: this.metricLabel(key),
          metricValue: this.metrics[key],
        }));
    },
    batchIsTerminal() {
      if (!this.batch.id) {
        return false;
      }
      const status = String(this.batch.status || '').toLowerCase();
      return TERMINAL_BATCH_STATUSES.includes(status)
        || (this.batch.total > 0 && this.batch.pending <= 0);
    },
    batchActive() {
      return Boolean(this.batch.id) && !this.batchIsTerminal;
    },
    closeDisabled() {
      return this.enqueueing;
    },
    batchProgress() {
      if (this.batch.total <= 0) {
        return 0;
      }
      return Math.min(100, ((this.batch.sent + this.batch.failed) / this.batch.total) * 100);
    },
    batchStatusColor() {
      if (this.batchIsTerminal) {
        return this.batch.failed > 0 ? 'warning' : 'success';
      }
      return this.statusError ? 'error' : 'primary';
    },
    batchStatusLabel() {
      if (this.batchIsTerminal) {
        return this.batch.failed > 0 ? 'Mit Fehlern abgeschlossen' : 'Abgeschlossen';
      }
      if (this.statusError) {
        return 'Statusabfrage unterbrochen';
      }
      return 'Wird versendet';
    },
  },
  created() {
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);
  },
  beforeDestroy() {
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);
    this.invalidateRequests();
  },
  watch: {
    dialog(open) {
      if (!open) {
        this.invalidateRequests();
      } else {
        this.labelDialogContent();
      }
    },
  },
  methods: {
    resetDialog() {
      this.invalidateRequests();
      this.step = 1;
      this.summaryError = '';
      this.recipientsError = '';
      this.editorError = '';
      this.enqueueError = '';
      this.statusError = '';
      this.summary = emptySummary();
      this.selectedSources = ['active_contracts'];
      this.recipientsLoaded = false;
      this.automaticRecipients = [];
      this.invalidBackendRecipients = [];
      this.metrics = {};
      this.missing = [];
      this.manualEntries = [];
      this.excludedEmails = [];
      this.subject = '';
      this.bodyHtml = '';
      this.confirmation = false;
      this.enqueuePayload = null;
      this.batch = emptyBatch();
      this.recoveryRequestId = '';
      this.pendingRecoveryCount = 0;
      this.mailFormValid = false;
      this.$nextTick(() => {
        if (this.$refs.mailForm) {
          this.$refs.mailForm.resetValidation();
        }
      });
    },
    async openDialog() {
      if (this.offline || !this.darfMassenmailVersenden) {
        return;
      }
      this.resetDialog();
      const generation = this.requestGeneration;
      this.dialog = true;
      if (await this.recoverPendingBatch(generation)) return;
      if (!this.dialog || generation !== this.requestGeneration) return;
      await this.loadSummary();
      if (this.dialog && generation === this.requestGeneration) {
        await this.loadRecipients();
      }
    },
    closeDialog() {
      if (this.closeDisabled) {
        return;
      }
      this.dialog = false;
    },
    handleOffline() {
      const pendingRequestId = this.enqueueing && this.enqueuePayload
        ? this.enqueuePayload.requestId : '';
      this.offline = true;
      this.invalidateRequests();
      if (pendingRequestId && this.activatePendingRecovery(
        pendingRequestId,
        'Die Verbindung wurde während des Versandstarts unterbrochen. Der Ausgang bleibt '
          + 'ungeklärt, bis der Server die Kennung eindeutig zugeordnet hat.',
      )) return;
      if (this.dialog) {
        this.statusError = this.batchActive
          ? 'Die Statusabfrage wurde wegen der fehlenden Netzwerkverbindung unterbrochen.' : '';
      }
    },
    handleOnline() {
      this.offline = false;
      if (this.dialog && this.recoveryRequestId && !this.recoveringPending) {
        this.retryRecovery();
      }
    },
    async sendRequest(action, fields = {}) {
      if (this.offline) {
        throw new Error('Der Server kann ohne Netzwerkverbindung nicht erreicht werden.');
      }
      const formData = new FormData();
      formData.append('code', this.$store.state.login.session.code);
      formData.append('action', action);
      Object.keys(fields).forEach((field) => {
        formData.append(field, fields[field]);
      });

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (controller) this.requestControllers.push(controller);
      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/400/410/massenmail.php`,
          {
            method: 'POST',
            body: formData,
            signal: controller ? controller.signal : undefined,
          },
        );
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        throw new Error('Der Server konnte nicht erreicht werden.');
      } finally {
        if (controller) {
          this.requestControllers = this.requestControllers
            .filter((candidate) => candidate !== controller);
        }
      }

      let result;
      try {
        result = await response.json();
      } catch (error) {
        throw new Error('Die Serverantwort konnte nicht verarbeitet werden.');
      }

      if (result && result.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        throw new Error(result.message || 'Die Sitzung ist nicht mehr gültig.');
      }
      if (result && result.status === 'forbidden') {
        throw new Error(result.message || 'Für den Massen-E-Mail-Versand fehlt die Berechtigung.');
      }
      if (!response.ok || !result || result.status !== 'success') {
        throw new Error(
          result && result.message
            ? result.message : 'Die Aktion konnte nicht ausgeführt werden.',
        );
      }
      return result;
    },
    async loadSummary() {
      const generation = this.requestGeneration;
      this.loadingSummary = true;
      this.summaryError = '';
      try {
        const result = await this.sendRequest('summary');
        if (generation !== this.requestGeneration || !this.dialog) return;
        const summary = result.summary && typeof result.summary === 'object'
          ? result.summary : {};
        this.summary = {
          fromName: String(summary.fromName || ''),
          fromMail: this.normalizeEmail(summary.fromMail || ''),
          smtpConfigured: this.toBoolean(summary.smtpConfigured),
          signatureHtml: String(summary.signatureHtml || ''),
          updatedAt: Math.max(0, Number(summary.updatedAt) || 0),
        };
      } catch (error) {
        if (generation !== this.requestGeneration || (error && error.name === 'AbortError')) return;
        this.summaryError = error.message || 'Die Versandkonfiguration konnte nicht geladen werden.';
      } finally {
        if (generation === this.requestGeneration) this.loadingSummary = false;
      }
    },
    markRecipientsStale() {
      this.recipientsLoaded = this.selectedSources.length <= 0;
      this.automaticRecipients = [];
      this.invalidBackendRecipients = [];
      this.metrics = {};
      this.missing = [];
      this.excludedEmails = [];
    },
    async loadRecipients() {
      if (this.selectedSources.length <= 0) {
        this.markRecipientsStale();
        return;
      }
      const generation = this.requestGeneration;
      this.loadingRecipients = true;
      this.recipientsError = '';
      try {
        const result = await this.sendRequest('recipients', {
          sources: JSON.stringify(this.selectedSources),
        });
        if (generation !== this.requestGeneration || !this.dialog) return;
        this.applyRecipients(result.recipients);
        this.metrics = result.metrics && typeof result.metrics === 'object'
          ? result.metrics : {};
        this.missing = Array.isArray(result.missing) ? result.missing : [];
        this.excludedEmails = [];
        this.recipientsLoaded = true;
      } catch (error) {
        if (generation !== this.requestGeneration || (error && error.name === 'AbortError')) return;
        this.automaticRecipients = [];
        this.invalidBackendRecipients = [];
        this.metrics = {};
        this.missing = [];
        this.recipientsLoaded = false;
        this.recipientsError = error.message || 'Die Empfänger konnten nicht ermittelt werden.';
      } finally {
        if (generation === this.requestGeneration) this.loadingRecipients = false;
      }
    },
    applyRecipients(recipients) {
      const validRecipients = [];
      const invalidRecipients = [];
      (Array.isArray(recipients) ? recipients : []).forEach((recipient) => {
        const email = this.normalizeEmail(recipient && recipient.email);
        if (!this.isValidEmail(email)) {
          invalidRecipients.push(recipient);
          return;
        }
        validRecipients.push({
          email,
          name: String(recipient.name || ''),
          customer: String(recipient.customer || ''),
          customerNumber: String(recipient.customerNumber || ''),
          sources: Array.isArray(recipient.sources) ? recipient.sources.map(String) : [],
        });
      });
      this.automaticRecipients = validRecipients;
      this.invalidBackendRecipients = invalidRecipients;
    },
    normalizeEmail(email) {
      return String(email || '').trim().toLowerCase();
    },
    isValidEmail(email) {
      const candidate = String(email || '').trim();
      const localPart = candidate.split('@')[0] || '';
      return candidate.length <= 254
        && localPart.length <= 64
        && !localPart.startsWith('.')
        && !localPart.endsWith('.')
        && !candidate.includes('..')
        && EMAIL_PATTERN.test(candidate);
    },
    isRecipientSelected(recipient) {
      return !this.excludedEmails.includes(this.normalizeEmail(recipient.email));
    },
    toggleRecipient(recipient) {
      const email = this.normalizeEmail(recipient.email);
      if (this.excludedEmails.includes(email)) {
        this.excludedEmails = this.excludedEmails.filter((entry) => entry !== email);
      } else {
        this.excludedEmails.push(email);
      }
      this.resetEnqueuePayload();
    },
    toggleAllRecipients() {
      if (this.allRecipientsSelected) {
        this.excludedEmails = this.allRecipients.map((recipient) => recipient.email);
      } else {
        const currentEmails = this.allRecipients.map((recipient) => recipient.email);
        this.excludedEmails = this.excludedEmails
          .filter((email) => !currentEmails.includes(email));
      }
      this.resetEnqueuePayload();
    },
    removeUnknownExclusions() {
      const knownEmails = this.allRecipients.map((recipient) => recipient.email);
      this.excludedEmails = this.excludedEmails
        .filter((email) => knownEmails.includes(email));
      this.resetEnqueuePayload();
    },
    sourceLabel(source) {
      const option = this.sourceOptions.find((entry) => entry.value === source);
      if (option) {
        return option.label;
      }
      return source === 'manual' ? 'Manuell' : source;
    },
    metricLabel(key) {
      const labels = {
        duplicates: 'Duplikate',
        invalid: 'Ungültig',
        missing: 'Ohne Adresse',
        total: 'Gefunden',
        unique: 'Eindeutig',
        duplicateCount: 'Duplikate',
        invalidCount: 'Ungültig',
        missingCount: 'Ohne Adresse',
        uniqueCount: 'Eindeutig',
        returnedCount: 'In Prüfliste',
        truncatedCount: 'Nicht aufgelistet',
      };
      return labels[key] || key;
    },
    missingLabel(entry) {
      if (typeof entry === 'string' || typeof entry === 'number') {
        return String(entry);
      }
      if (!entry || typeof entry !== 'object') {
        return 'Unbekannter Eintrag';
      }
      const reference = [
        entry.customer,
        entry.customerNumber ? `Kundennr. ${entry.customerNumber}` : '',
        entry.contract ? `Vertrag ${entry.contract}` : '',
      ].filter(Boolean).join(' – ');
      if (reference && entry.reason) return `${reference}: ${entry.reason}`;
      return reference || entry.reason || entry.name || JSON.stringify(entry);
    },
    toBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },
    handleEditorError(message) {
      this.editorError = message;
    },
    goToContent() {
      if (!this.canContinueToContent) {
        return;
      }
      this.step = 2;
      this.confirmation = false;
      this.resetEnqueuePayload();
    },
    goToReview() {
      if (this.$refs.mailForm && !this.$refs.mailForm.validate()) {
        return;
      }
      if (!this.canContinueToReview) {
        return;
      }
      this.step = 3;
      this.confirmation = false;
      this.resetEnqueuePayload();
    },
    goBack() {
      if (!this.resetEnqueuePayload()) return;
      if (this.step === 3) {
        this.step = 2;
      } else if (this.step === 2) {
        this.step = 1;
      }
      this.confirmation = false;
    },
    resetEnqueuePayload() {
      const requestId = this.enqueuePayload && this.enqueuePayload.requestId
        ? this.enqueuePayload.requestId : '';
      if (requestId && this.activatePendingRecovery(
        requestId,
        'Dieser Versandstart wurde bereits an den Server übermittelt. Er muss zuerst '
          + 'eindeutig geklärt oder ausdrücklich verworfen werden.',
        500,
      )) return false;
      this.enqueuePayload = null;
      this.enqueueError = '';
      return true;
    },
    createRequestId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        const values = new Uint32Array(4);
        window.crypto.getRandomValues(values);
        return Array.from(values)
          .map((value) => value.toString(16).padStart(8, '0'))
          .join('-');
      }
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
    pendingStoragePrefix() {
      return 'ogsmyadmin_f410_mass_mail_pending:';
    },
    legacyPendingStorageKey() {
      return 'ogsmyadmin_f410_mass_mail_pending';
    },
    pendingStorageKey(requestId) {
      return `${this.pendingStoragePrefix()}${requestId}`;
    },
    normalizePendingRecord(record) {
      if (!record || typeof record.requestId !== 'string'
        || !/^[A-Za-z0-9._:-]{8,128}$/.test(record.requestId)) {
        return null;
      }
      const createdAt = Number(record.createdAt);
      if (!Number.isFinite(createdAt)
        || createdAt > Date.now() + (5 * 60 * 1000)) {
        return null;
      }
      return {
        requestId: record.requestId,
        batchId: String(record.batchId || ''),
        createdAt,
        updatedAt: Number(record.updatedAt) || createdAt,
      };
    },
    readPendingRecords() {
      const records = [];
      try {
        const legacyKey = this.legacyPendingStorageKey();
        const legacyRaw = window.localStorage.getItem(legacyKey);
        if (legacyRaw) {
          let legacyRecord = null;
          try {
            legacyRecord = this.normalizePendingRecord(JSON.parse(legacyRaw));
          } catch (error) {
            legacyRecord = null;
          }
          if (legacyRecord) {
            this.writePendingRecord(legacyRecord.requestId, legacyRecord.batchId,
              legacyRecord.createdAt);
          }
          window.localStorage.removeItem(legacyKey);
        }

        const prefix = this.pendingStoragePrefix();
        const keys = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
          const key = window.localStorage.key(index);
          if (key && key.startsWith(prefix)) keys.push(key);
        }
        keys.forEach((key) => {
          let record = null;
          try {
            record = this.normalizePendingRecord(JSON.parse(window.localStorage.getItem(key)));
          } catch (error) {
            record = null;
          }
          if (record && key === this.pendingStorageKey(record.requestId)) {
            records.push(record);
          } else {
            window.localStorage.removeItem(key);
          }
        });
      } catch (error) {
        return [];
      }
      return records.sort((left, right) => {
        const batchPriority = Number(Boolean(right.batchId)) - Number(Boolean(left.batchId));
        return batchPriority || right.updatedAt - left.updatedAt;
      });
    },
    writePendingRecord(requestId, batchId = '', originalCreatedAt = 0) {
      try {
        const key = this.pendingStorageKey(requestId);
        let current = null;
        try {
          current = this.normalizePendingRecord(JSON.parse(window.localStorage.getItem(key)));
        } catch (error) {
          current = null;
        }
        window.localStorage.setItem(key, JSON.stringify({
          requestId,
          batchId: String(batchId || ''),
          createdAt: current ? current.createdAt : (Number(originalCreatedAt) || Date.now()),
          updatedAt: Date.now(),
        }));
        return true;
      } catch (error) {
        // Der Server bleibt ueber die requestId idempotent; Storage kann gesperrt sein.
        return false;
      }
    },
    clearPendingRecord(requestId) {
      if (!requestId) return;
      try {
        window.localStorage.removeItem(this.pendingStorageKey(requestId));
      } catch (error) {
        // Kein funktionaler Fehler, wenn Browser-Storage nicht verfuegbar ist.
      }
    },
    async recoverPendingBatch(generation, requestedId = '') {
      const pendingRecords = this.readPendingRecords();
      let pending = requestedId
        ? pendingRecords.find((record) => record.requestId === requestedId)
        : pendingRecords[0];
      if (!pending && requestedId && requestedId === this.recoveryRequestId) {
        pending = { requestId: requestedId, batchId: '', createdAt: Date.now() };
        this.writePendingRecord(requestedId, '', pending.createdAt);
      }
      if (!pending) return false;
      this.recoveryRequestId = pending.requestId;
      this.pendingRecoveryCount = Math.max(1, pendingRecords.length);
      this.enqueuePayload = { requestId: pending.requestId };
      this.step = 4;
      this.recoveringPending = true;
      this.statusError = '';
      try {
        const result = await this.sendRequest('recover', { requestId: pending.requestId });
        if (generation !== this.requestGeneration || !this.dialog) return true;
        if (result.batch && result.batch.id) {
          this.batch = this.normalizeBatch(result.batch);
          this.recoveryRequestId = '';
          if (this.batchIsTerminal) {
            this.clearPendingRecord(pending.requestId);
          } else {
            this.writePendingRecord(pending.requestId, this.batch.id);
            this.schedulePolling(500);
          }
          this.pendingRecoveryCount = this.readPendingRecords().length;
          return true;
        }
        this.statusError = 'Der Server kennt zu dieser Kennung noch keinen Versand. '
          + 'Die Prüfung wird automatisch fortgesetzt.';
        this.scheduleRecovery(generation, pending.requestId, 5000);
        return true;
      } catch (error) {
        if (generation !== this.requestGeneration || (error && error.name === 'AbortError')) {
          return true;
        }
        this.statusError = 'Der Server konnte zur Klärung nicht erreicht werden. '
          + 'Bitte stellen Sie die Verbindung wieder her und prüfen Sie erneut.';
        return true;
      } finally {
        if (generation === this.requestGeneration) this.recoveringPending = false;
      }
    },
    scheduleRecovery(generation, requestId, delay = 10000) {
      this.stopPolling();
      if (!this.dialog || this.offline || !requestId) return;
      this.pollTimer = window.setTimeout(() => {
        if (generation === this.requestGeneration) {
          this.recoverPendingBatch(generation, requestId);
        }
      }, delay);
    },
    retryRecovery() {
      if (this.recoveringPending || this.offline || !this.recoveryRequestId) return;
      this.stopPolling();
      this.recoverPendingBatch(this.requestGeneration, this.recoveryRequestId);
    },
    async discardPendingRecovery() {
      if (!this.recoveryRequestId || this.recoveringPending) return;
      const confirmed = window.confirm('Nur fortfahren, wenn Sie sicher sind, dass der vorherige '
        + 'Versand nicht mehr verarbeitet wird. Andernfalls könnten Empfänger eine E-Mail '
        + 'doppelt erhalten. Ungeklärten Versandstart wirklich verwerfen?');
      if (!confirmed) return;
      const requestId = this.recoveryRequestId;
      this.clearPendingRecord(requestId);
      this.resetDialog();
      const generation = this.requestGeneration;
      if (await this.recoverPendingBatch(generation)) return;
      if (!this.dialog || generation !== this.requestGeneration) return;
      await this.loadSummary();
      if (this.dialog && generation === this.requestGeneration) await this.loadRecipients();
    },
    activatePendingRecovery(requestId, message, retryDelay = 0) {
      const pendingRecords = this.readPendingRecords();
      const pending = pendingRecords.find((record) => record.requestId === requestId);
      const isCurrentRequest = this.enqueuePayload
        && this.enqueuePayload.requestId === requestId;
      if (!pending && !isCurrentRequest) return false;
      this.batch = emptyBatch();
      this.recoveryRequestId = requestId;
      this.pendingRecoveryCount = Math.max(1, pendingRecords.length);
      this.enqueuePayload = { requestId };
      this.enqueueError = '';
      this.confirmation = false;
      this.statusError = message || 'Der Ausgang dieses Versandstarts ist noch ungeklärt.';
      this.step = 4;
      if (!this.offline && retryDelay > 0) {
        this.scheduleRecovery(this.requestGeneration, requestId, retryDelay);
      }
      return true;
    },
    buildEnqueuePayload() {
      return {
        requestId: this.createRequestId(),
        confirmed: '1',
        subject: this.subject.trim(),
        bodyHtml: this.bodyHtml,
        expectedUpdatedAt: String(this.summary.updatedAt),
        recipients: JSON.stringify(this.selectedRecipients.map((recipient) => ({
          email: recipient.email,
          name: recipient.name,
          customer: recipient.customer,
          customerNumber: recipient.customerNumber,
          sources: recipient.sources,
        }))),
      };
    },
    async enqueue() {
      if (!this.canEnqueue) {
        return;
      }
      if (!this.enqueuePayload) {
        this.enqueuePayload = this.buildEnqueuePayload();
      }
      if (!this.writePendingRecord(this.enqueuePayload.requestId, this.batch.id)) {
        this.enqueueError = 'Der sichere Wiederaufnahmestatus konnte im Browser nicht gespeichert '
          + 'werden. Der Versand wurde nicht gestartet. Bitte erlauben Sie lokalen Speicher und '
          + 'versuchen Sie es erneut.';
        this.enqueuePayload = null;
        return;
      }
      const generation = this.requestGeneration;
      this.enqueueing = true;
      this.enqueueError = '';
      try {
        const result = await this.sendRequest('enqueue', this.enqueuePayload);
        if (generation !== this.requestGeneration || !this.dialog) return;
        this.batch = this.normalizeBatch(result.batch);
        this.writePendingRecord(this.enqueuePayload.requestId, this.batch.id);
        this.step = 4;
        if (!this.batch.id) {
          this.statusError = 'Der Server hat keine Versand-ID geliefert.';
        } else if (!this.batchIsTerminal) {
          this.schedulePolling(700);
        } else {
          this.clearPendingRecord(this.enqueuePayload.requestId);
        }
      } catch (error) {
        if (generation !== this.requestGeneration || (error && error.name === 'AbortError')) return;
        const message = error.message || 'Der Versand konnte nicht angestoßen werden.';
        this.activatePendingRecovery(
          this.enqueuePayload.requestId,
          `${message} Der Ausgang des Versandstarts wird deshalb als ungeklärt behandelt; `
            + 'ein neuer Versand bleibt bis zur Klärung gesperrt.',
          3000,
        );
      } finally {
        if (generation === this.requestGeneration) this.enqueueing = false;
      }
    },
    normalizeBatch(batch) {
      const rawBatch = batch && typeof batch === 'object' ? batch : {};
      const total = Math.max(0, Number(rawBatch.total) || 0);
      const sent = Math.max(0, Number(rawBatch.sent) || 0);
      const failed = Math.max(0, Number(rawBatch.failed) || 0);
      const pendingValue = rawBatch.pending === undefined || rawBatch.pending === null
        ? total - sent - failed : Number(rawBatch.pending);
      return {
        id: String(rawBatch.id || ''),
        total,
        sent,
        failed,
        pending: Math.max(0, pendingValue || 0),
        status: String(rawBatch.status || ''),
      };
    },
    schedulePolling(delay = 1500) {
      this.stopPolling();
      if (!this.dialog || this.offline || this.batchIsTerminal || !this.batch.id) {
        return;
      }
      const generation = this.requestGeneration;
      this.pollTimer = window.setTimeout(() => {
        if (generation === this.requestGeneration) this.pollBatch();
      }, delay);
    },
    stopPolling() {
      if (this.pollTimer !== null) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    invalidateRequests() {
      this.requestGeneration += 1;
      this.stopPolling();
      this.requestControllers.forEach((controller) => controller.abort());
      this.requestControllers = [];
      this.loadingSummary = false;
      this.loadingRecipients = false;
      this.enqueueing = false;
      this.polling = false;
      this.recoveringPending = false;
    },
    labelDialogContent() {
      this.$nextTick(() => {
        const dialogComponent = this.$refs.massenDialog;
        const content = dialogComponent && dialogComponent.$refs
          ? dialogComponent.$refs.content : null;
        if (content) content.setAttribute('aria-labelledby', 'massen-email-dialog-title');
      });
    },
    async pollBatch() {
      this.stopPolling();
      if (!this.dialog || this.offline || this.batchIsTerminal || !this.batch.id) {
        return;
      }
      const generation = this.requestGeneration;
      this.polling = true;
      this.statusError = '';
      try {
        const result = await this.sendRequest('status', {
          batchId: this.batch.id,
          process: '1',
        });
        if (generation !== this.requestGeneration || !this.dialog) return;
        this.batch = this.normalizeBatch(result.batch);
        const requestId = this.enqueuePayload && this.enqueuePayload.requestId
          ? this.enqueuePayload.requestId : '';
        if (!this.batchIsTerminal) {
          if (requestId) this.writePendingRecord(requestId, this.batch.id);
          this.schedulePolling();
        } else if (requestId) {
          this.clearPendingRecord(requestId);
        }
      } catch (error) {
        if (generation !== this.requestGeneration || (error && error.name === 'AbortError')) return;
        this.statusError = error.message || 'Der Versandstatus konnte nicht geladen werden.';
      } finally {
        if (generation === this.requestGeneration) this.polling = false;
      }
    },
    retryStatus() {
      if (!this.polling && !this.offline) {
        this.pollBatch();
      }
    },
    previewDocument(content) {
      return `<!doctype html>
        <html lang="de">
          <head>
            <meta charset="utf-8">
            <meta name="referrer" content="no-referrer">
            <style>
              body { color: #222; font-family: Arial, sans-serif; font-size: 14px;
                line-height: 1.45; margin: 12px; overflow-wrap: anywhere; }
              img { height: auto; max-width: 100%; }
              .signature { border-top: 1px solid #ddd; margin-top: 24px; padding-top: 16px; }
            </style>
          </head>
          <body>${content}</body>
        </html>`;
    },
  },
};
</script>

<style scoped>
.massen-email-dialog {
  vertical-align: middle;
}

.mail-preview-frame {
  width: 100%;
  min-height: 230px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: #fff;
}

.mail-preview-frame--message {
  min-height: 340px;
}
</style>
